// ÖSSZEGZŐ KÁRTYÁK — a lista-oldalak szűrő-sáv és lista közötti sávjának KANONIKUS alakja: balra a PÉNZ,
// jobbra az ÁLLAPOT. Mobilon egymás alá csúsznak, desktopon MINDIG azonos magasak.
//
// ══ MIÉRT KÖZÖS RÉTEG ════════════════════════════════════════════════════════════════════════════════════
// Két lista használja (Kiadások · Bevételek), és a Pénzügy lap KÉT FÜLÉRŐL van szó — ha a váz két helyen
// élne, a két fül előbb-utóbb máshogy nézne ki. Ami KÖZÖS: a rács, a kártya-héj, az azonos magasság és az
// állapot-sor alakja. Ami FEATURE-SPECIFIKUS (mit írunk bele), az a hívóé marad.
//
// ══ MIÉRT NEM EGY VEGYES JELVÉNY-CSÍK (a korábbi alak) ═══════════════════════════════════════════════════
// A pénz-kártyák és az állapot-jelvények egyetlen flex-sor testvérei voltak, azonos térközzel — tehát
// egyetlen, tagolatlan csíkként olvasódtak, pedig KÉT KÜLÖN kérdésre felelnek: „mennyi?" és „mire kell
// figyelni?". A jelvény-sor ráadásul a pénz-kártyák adat-függő szélessége mögött hónapról hónapra máshova
// tördelődött; mobilon kártya és jelvény került egy sorba.
import { type ReactNode } from 'react';

import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';

/**
 * Egy ÁLLAPOT-sor: címke balra, érték jobbra.
 *
 * A `tone` a sor FAJTÁJÁT mondja ki, és ez SZÁNDÉKOSAN nem egyforma — ha minden sor azonos szürke
 * meta-szöveg lenne, a cselekvést kérő tételek nem „húznának" jobban egy passzív időpontnál:
 *   • `warning` → TEENDŐ (besorolásra vár, hiányzó árfolyam, …),
 *   • `muted`   → PASSZÍV tény (utolsó import, forrás),
 *   • `default` → semleges érték.
 */
export function SummaryMetaRow({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: ReactNode;
  tone?: 'default' | 'warning' | 'muted';
}): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <Text variant="secondary" as="span" className="min-w-0 truncate text-xs">
        {label}
      </Text>
      <span
        className={cn(
          'shrink-0 text-sm tabular-nums',
          tone === 'warning' && 'text-warning font-medium',
          tone === 'muted' && 'text-subtle text-xs',
          tone === 'default' && 'text-foreground',
        )}
      >
        {value}
      </span>
    </div>
  );
}

/** Egy kártya: overline-cím + tartalom. A `h-full` adja az azonos magasságot a rács `items-stretch`-ével. */
export function SummaryCard({
  title,
  full = false,
  children,
}: {
  title: string;
  /** A párja hiányzik → a kártya a teljes szélességet kapja (nem marad fél sor üresen). */
  full?: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <section
      className={cn('card-glass h-full rounded-xl p-4', full && 'md:col-span-2')}
      aria-label={title}
    >
      <Text variant="overline" as="div" className="mb-3">
        {title}
      </Text>
      {children}
    </section>
  );
}

/**
 * A KÉT KÁRTYA rácsa. A pénz-oldal szélesebb (ott áll a nagy szám és a deviza-hasábok), az állapot keskenyebb.
 * `items-stretch`: desktopon a két kártya azonos magas akkor is, ha különböző mennyiségű sort tart —
 * enélkül a rövidebb „lebegett" a sorban.
 */
export function SummaryCards({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="grid items-stretch gap-3 md:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
      {children}
    </div>
  );
}
